// Release Manager — Fase 15.5
export type RolloutStatus = 'draft' | 'active' | 'paused' | 'rolled_back' | 'completed'

export interface FeatureRollout {
  id: string; featureKey: string; description: string; status: RolloutStatus
  targetPct: number; currentPct: number; startedAt: string | null; completedAt: string | null
  rollbackAt: string | null; rollbackReason: string | null; compatibilityOk: boolean
}
export interface RollbackMarker {
  id: string; version: string; createdAt: string; snapshot: string; reason: string
  environment: string; validated: boolean
}
export interface CompatibilityReport {
  timestamp: string; appVersion: string; compatible: boolean
  checks: Array<{ name: string; ok: boolean; message: string }>
}

let _rollouts: FeatureRollout[] = []
let _markers:  RollbackMarker[] = []
let _globalKill = false
function gid(p: string): string { return `${p}_${Date.now()}_${Math.random().toString(36).slice(2,7)}` }

export function registerRollout(p: { featureKey: string; description: string; targetPct?: number }): FeatureRollout {
  const r: FeatureRollout = { id: gid('ro'), featureKey: p.featureKey, description: p.description,
    status: 'draft', targetPct: p.targetPct ?? 100, currentPct: 0,
    startedAt: null, completedAt: null, rollbackAt: null, rollbackReason: null, compatibilityOk: true }
  _rollouts.push(r); return r
}
export function activateRollout(id: string, initialPct = 5): void {
  const r = _rollouts.find(r => r.id === id); if (!r || _globalKill) return
  r.status = 'active'; r.currentPct = initialPct; r.startedAt = new Date().toISOString()
}
export function advanceRollout(id: string, pct: number): void {
  const r = _rollouts.find(r => r.id === id); if (!r || r.status !== 'active' || _globalKill) return
  r.currentPct = Math.min(pct, r.targetPct)
  if (r.currentPct >= r.targetPct) { r.status = 'completed'; r.completedAt = new Date().toISOString() }
}
export function rollbackFeature(id: string, reason: string): void {
  const r = _rollouts.find(r => r.id === id); if (!r) return
  r.status = 'rolled_back'; r.rollbackAt = new Date().toISOString(); r.rollbackReason = reason; r.currentPct = 0
}
export function pauseRollout(id: string): void {
  const r = _rollouts.find(r => r.id === id); if (r && r.status === 'active') r.status = 'paused'
}
export function activateGlobalKillSwitch(): void { _globalKill = true  }
export function clearGlobalKillSwitch(): void    { _globalKill = false }
export function isGlobalKillActive(): boolean    { return _globalKill  }
export function getRolloutPct(featureKey: string): number {
  if (_globalKill) return 0
  return _rollouts.find(r => r.featureKey === featureKey && r.status === 'active')?.currentPct ?? 0
}
export function isRolloutActive(featureKey: string): boolean {
  return !_globalKill && _rollouts.find(r => r.featureKey === featureKey)?.status === 'active'
}
export function registerRollbackMarker(p: { version: string; reason: string; environment: string; snapshot: string }): RollbackMarker {
  const m: RollbackMarker = { ...p, id: gid('rbm'), createdAt: new Date().toISOString(), validated: false }
  _markers.push(m); return m
}
export function validateRollbackMarker(id: string): void {
  const m = _markers.find(m => m.id === id); if (m) m.validated = true
}
export function getLatestRollbackMarker(): RollbackMarker | null {
  return [..._markers].sort((a,b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
}
export function runCompatibilityCheck(): CompatibilityReport {
  const appVersion = (import.meta.env['VITE_APP_VERSION'] as string | undefined) ?? '0.0.0'
  const checks: Array<{ name: string; ok: boolean; message: string }> = []
  checks.push({ name: 'app_version_set',       ok: appVersion !== '0.0.0', message: appVersion !== '0.0.0' ? `Version: ${appVersion}` : 'App version not set' })
  checks.push({ name: 'supabase_url',          ok: !!import.meta.env['VITE_SUPABASE_URL'], message: import.meta.env['VITE_SUPABASE_URL'] ? 'Supabase URL set' : 'Missing VITE_SUPABASE_URL' })
  const lsOk = (() => { try { localStorage.setItem('_c','1'); localStorage.removeItem('_c'); return true } catch { return false } })()
  checks.push({ name: 'localStorage',          ok: lsOk, message: lsOk ? 'localStorage accessible' : 'localStorage unavailable' })
  checks.push({ name: 'websocket',             ok: typeof WebSocket !== 'undefined', message: typeof WebSocket !== 'undefined' ? 'WebSocket available' : 'WebSocket unavailable' })
  const hasMarker = _markers.some(m => m.validated)
  checks.push({ name: 'rollback_marker_ready', ok: hasMarker, message: hasMarker ? 'Validated rollback marker present' : 'No validated rollback marker' })
  return { timestamp: new Date().toISOString(), appVersion, compatible: checks.every(c => c.ok), checks }
}
export function getRollouts(): FeatureRollout[]  { return [..._rollouts] }
export function getMarkers():  RollbackMarker[]  { return [..._markers]  }
export function resetReleaseManager(): void      { _rollouts = []; _markers = []; _globalKill = false }
